// @ts-nocheck
// TODO: tighten types after migration
import React from 'react';

/**
 * ProgressBorder Component
 * Renders an SVG border that animates based on a progress percentage.
 * The border traces the perimeter starting from the top-middle.
 * 
 * @param {Object} props
 * @param {number} props.width - Width of the container
 * @param {number} props.height - Height of the container
 * @param {number} props.progress - Progress percentage (0-100)
 * @param {string} props.color - Border color (default: #47b5ff)
 * @param {number} props.strokeWidth - Border thickness (default: 5)
 * @param {number} props.borderRadius - Radius for corners (approximate for path)
 */
const ProgressBorder = ({ 
    width, 
    height, 
    progress, 
    color = '#47b5ff', 
    strokeWidth = 5,
    borderRadius = 0 
}) => {
    // Calculate perimeter
    // Note: We are using a simple path logic. If borderRadius is significant, 
    // exact perimeter calc is complex, but linear approximation is usually fine for visual loaders.
    // For exactness with rounded corners, we would need arcs. 
    // Given the request mentions "border/outline", and the loaders are rects (book) or rounded rects (tape).
    // Let's stick to a rect path for simplicity, relying on SVG `rx` if we used <rect>, 
    // but to start from top-middle, <path> is better.
    
    // Path strategy:
    // Move to top-middle (width/2, 0)
    // Draw clockwise to completion.
    
    // To handle rounded corners easily with "start from middle", we can actually use a <rect> 
    // and rely on stroke-dashoffset to shift the start point.
    // A standard rect path starts at (0,0) -> top-left.
    // Perimeter P.
    // We want start at (width/2, 0). 
    // Distance from (0,0) to (width/2, 0) is width/2.
    // So we offset by -width/2 ? 
    // No, SVG coord system is tricky with offsets.
    
    // Let's use the explicit path segment approach for robust "start from top-middle" behavior
    // and manual rounded corners if needed, or just sharp corners if radius is 0.
    
    // Simplification: The user asked for "border/outline".
    // Using a rect with pathLength property is easiest for percentage, 
    // but starting point is fixed at top-left for rects in most browsers without rotation.
    // If we rotate -90deg, it starts at bottom-left? No.
    
    // Let's build the path manually. 
    // M w/2 0 -> L w-r 0 -> A ... -> L w h-r -> ...
    // This is robust.
    
    const r = borderRadius;
    const w = width;
    const h = height;
    
    // Top-Middle start
    const d = `
        M ${w / 2} 0
        L ${w - r} 0
        ${r > 0 ? `Q ${w} 0 ${w} ${r}` : `L ${w} 0`}
        L ${w} ${h - r}
        ${r > 0 ? `Q ${w} ${h} ${w - r} ${h}` : `L ${w} ${h}`}
        L ${r} ${h}
        ${r > 0 ? `Q 0 ${h} 0 ${h - r}` : `L 0 ${h}`}
        L 0 ${r}
        ${r > 0 ? `Q 0 0 ${r} 0` : `L 0 0`}
        L ${w / 2} 0
    `.replace(/\s+/g, ' ').trim();

    // Approximate perimeter for dasharray
    // 2*w + 2*h - 8*r + 2*PI*r  = 2w + 2h - r(8 - 2PI)
    // Actually simpler: straight parts + 4 corners (2*PI*r total? No, 4 * 1/4 circle = 2*PI*r)
    // Straight parts: (w/2 - r) + (w - 2r) + (h - 2r) + (w - 2r) + (h - 2r) + (w/2 - r)? 
    // Let's just use path.getTotalLength() via ref if possible, 
    // but in React render we don't have ref yet.
    // Math calculation:
    // Top-Right half: w/2
    // Right: h
    // Bottom: w
    // Left: h
    // Top-Left half: w/2
    // Total = 2w + 2h.
    // (With rounded corners subtraction: -8r + 2*PI*r)
    // 8r is removed from straight lines (2r per corner, except start/end split? handled correctly).
    // Perimeter P = 2 * (w + h) - (8 * r) + (2 * Math.PI * r);
    
    const perimeter = 2 * (w + h) - (8 * r) + (2 * Math.PI * r);
    const strokeLength = (progress / 100) * perimeter;
    
    // We need 'gap' to be the rest of perimeter.
    // stroke-dasharray: [visible, invisible]
    // visible = strokeLength
    // invisible = perimeter - strokeLength (plus a bit to be safe)

    return (
        <div 
            style={{
                position: 'absolute',
                top: -strokeWidth,
                left: -strokeWidth,
                width: width + strokeWidth * 2,
                height: height + strokeWidth * 2,
                pointerEvents: 'none',
                zIndex: 10
            }}
        >
            <svg 
                width="100%" 
                height="100%" 
                viewBox={`-${strokeWidth} -${strokeWidth} ${width + strokeWidth * 2} ${height + strokeWidth * 2}`}
                style={{ overflow: 'visible' }}
            >
                {/* Background track (optional, maybe not needed per request, effectively "0%" is transparent) */}
                
                {/* Progress Path */}
                <path
                    d={d}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round" 
                    strokeDasharray={`${strokeLength} ${perimeter}`}
                    strokeDashoffset={0}
                    style={{ transition: 'stroke-dasharray 0.1s linear' }}
                />
            </svg>
        </div>
    );
};

export default ProgressBorder;
